import React from 'react';
import { MenuItem } from '../types';
import { ChevronRight, Utensils, Plus, X } from 'lucide-react';

// Cuisine Cards Display
interface CuisineCardsProps {
    cuisines: string[];
    onSelect: (cuisine: string) => void;
    onCancel: () => void;
}

export const CuisineCards: React.FC<CuisineCardsProps> = ({ cuisines, onSelect, onCancel }) => {
    const cuisineIcons: Record<string, string> = {
        'Pakistani': '🍛',
        'Chinese': '🥢',
        'Continental': '🍽️',
        'Fast Food': '🍔',
        'Beverages': '🥤',
        'Desserts': '🍰'
    };

    return (
        <div className="h-full flex flex-col bg-gradient-to-br from-mocha-light to-white">
            <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm">
                <div>
                    <h3 className="font-serif font-bold text-2xl text-gray-900">Explore Our Cuisines</h3>
                    <p className="text-xs text-terracotta font-bold tracking-widest uppercase mt-1.5">Choose Your Flavor Journey</p>
                </div>
                <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all">
                    <X size={24} />
                </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 custom-scrollbar">
                <div className="grid grid-cols-2 gap-4">
                    {cuisines.map((cuisine, idx) => (
                        <button
                            key={cuisine}
                            onClick={() => onSelect(cuisine)}
                            className="group relative bg-white border-2 border-gray-100 hover:border-terracotta rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 animate-in zoom-in-95 fade-in"
                            style={{ animationDelay: `${idx * 0.1}s` }}
                        >
                            <div className="text-5xl mb-3">{cuisineIcons[cuisine] || '🍴'}</div>
                            <h4 className="font-serif font-bold text-lg text-gray-900 group-hover:text-terracotta transition-colors">{cuisine}</h4>
                            <ChevronRight className="absolute bottom-4 right-4 text-gray-300 group-hover:text-terracotta group-hover:translate-x-1 transition-all" size={20} />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Category Cards Display
interface CategoryCardsProps {
    cuisine: string;
    categories: string[];
    onSelect: (category: string) => void;
    onBack: () => void;
    onCancel: () => void;
}

export const CategoryCards: React.FC<CategoryCardsProps> = ({ cuisine, categories, onSelect, onBack, onCancel }) => {
    return (
        <div className="h-full flex flex-col bg-gradient-to-br from-mocha-light to-white">
            <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm">
                <div>
                    <button onClick={onBack} className="text-xs text-terracotta font-bold mb-1 flex items-center gap-1 hover:gap-2 transition-all">
                        <ChevronRight size={14} className="rotate-180" /> Back to Cuisines
                    </button>
                    <h3 className="font-serif font-bold text-2xl text-gray-900">{cuisine} Menu</h3>
                    <p className="text-xs text-gray-500 mt-1">Select a category to explore</p>
                </div>
                <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all">
                    <X size={24} />
                </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 custom-scrollbar">
                <div className="grid grid-cols-1 gap-3">
                    {categories.map((category, idx) => (
                        <button
                            key={category}
                            onClick={() => onSelect(category)}
                            className="group bg-white border-2 border-gray-100 hover:border-terracotta rounded-xl p-5 transition-all duration-300 hover:shadow-lg hover:translate-x-2 animate-in slide-in-from-left-4 fade-in flex items-center justify-between"
                            style={{ animationDelay: `${idx * 0.08}s` }}
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center">
                                    <Utensils className="text-terracotta" size={20} />
                                </div>
                                <h4 className="font-serif font-bold text-lg text-gray-900 group-hover:text-terracotta transition-colors">{category}</h4>
                            </div>
                            <ChevronRight className="text-gray-300 group-hover:text-terracotta group-hover:translate-x-1 transition-all" size={24} />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Item Detail Modal
interface ItemDetailProps {
    item: MenuItem;
    onAddToCart: (item: MenuItem) => void;
    onBack: () => void;
    onCancel: () => void;
}

export const ItemDetail: React.FC<ItemDetailProps> = ({ item, onAddToCart, onBack, onCancel }) => {
    return (
        <div className="h-full flex flex-col bg-white">
            <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm">
                <button onClick={onBack} className="text-xs text-terracotta font-bold flex items-center gap-1 hover:gap-2 transition-all">
                    <ChevronRight size={14} className="rotate-180" /> Back to Menu
                </button>
                <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all">
                    <X size={24} />
                </button>
            </div>

            <div className="flex-grow overflow-y-auto custom-scrollbar">
                <div className="relative h-64 bg-gray-100">
                    {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Utensils size={64} />
                        </div>
                    )}
                    {item.popular && (
                        <span className="absolute top-4 left-4 bg-gold text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                            POPULAR
                        </span>
                    )}
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <h2 className="font-serif text-3xl font-bold text-gray-900 mb-2">{item.name}</h2>
                        <p className="text-4xl font-bold text-terracotta">₨{item.price}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <p className="text-gray-700 leading-relaxed">{item.description}</p>
                    </div>

                    {item.dietary && item.dietary.length > 0 && (
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase mb-2">Dietary Information</p>
                            <div className="flex flex-wrap gap-2">
                                {item.dietary.map(d => (
                                    <span key={d} className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold border border-green-200">
                                        {d === 'GF' ? 'Gluten Free' : d === 'V' ? 'Vegetarian' : 'Vegan'}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">Category</p>
                            <p className="font-bold text-gray-900">{item.category}</p>
                        </div>
                        {item.cuisine && (
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Cuisine</p>
                                <p className="font-bold text-gray-900">{item.cuisine}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-shrink-0 p-6 bg-white border-t border-gray-100">
                <button
                    onClick={() => onAddToCart(item)}
                    className="w-full bg-terracotta hover:bg-terracotta-dark text-white py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-2 group"
                >
                    <Plus size={20} className="group-hover:rotate-90 transition-transform" />
                    Add to Order - ₨{item.price}
                </button>
            </div>
        </div>
    );
};
